import { createSignal, onCleanup } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { vibrate } from "@/lib/gestures/haptics";
import { CALENDAR_SWIPE } from "@/lib/gestures/presets";

// oxlint-disable-next-line lingui/no-unlocalized-strings -- CSS media query, not UI copy
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

interface DaySwipeGesturesOptions {
  /** Page to a previous/next day. delta<0 = previous, delta>0 = next. */
  onShiftDay: (delta: number) => void;
  /** Whether paging forward (toward newer days) is currently allowed. */
  canGoNext: () => boolean;
  getSurfaceRef: () => HTMLElement | undefined;
}

/**
 * Horizontal day carousel for the today-view, on the SAME standard as the
 * calendar month pager (`CALENDAR_SWIPE` preset + shared recognizer physics).
 * Swipe right reveals the previous day, swipe left the next day — but never past
 * today (`canGoNext` gates the forward direction, both at commit and with a
 * rubber-band on the drag so the edge feels intentional).
 *
 * Marked `data-swipe-own` by its surface so the top-level tab page-swipe yields
 * the horizontal axis to it. A drag that begins on a `<button>`/`<a>` is ignored
 * so the per-row controls and the row's own swipe-to-action keep working.
 */
export function useDaySwipeGestures(options: DaySwipeGesturesOptions) {
  const [dragX, setDragX] = createSignal(0);
  const [transitioning, setTransitioning] = createSignal(false);
  let swiping = false;

  const surfaceWidth = () => options.getSurfaceRef()?.clientWidth ?? CALENDAR_SWIPE.fallbackWidthPx;

  /** Animate a directional day change. delta>0 = next (current slides left). */
  const animateDay = (delta: number) => {
    if (delta === 0) {
      return;
    }
    if (delta > 0 && !options.canGoNext()) {
      snapBack();
      return;
    }
    if (prefersReducedMotion()) {
      options.onShiftDay(delta);
      setDragX(0);
      return;
    }
    const width = surfaceWidth();
    const outX = delta > 0 ? -width : width;
    setTransitioning(true);
    setDragX(outX);
    vibrate(CALENDAR_SWIPE.hapticMs);
    globalThis.setTimeout(() => {
      options.onShiftDay(delta);
      setTransitioning(false);
      setDragX(-outX);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitioning(true);
          setDragX(0);
        });
      });
    }, CALENDAR_SWIPE.transitionMs);
  };

  function snapBack() {
    setTransitioning(true);
    setDragX(0);
  }

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: CALENDAR_SWIPE.thresholdPx,
    fling: { ...CALENDAR_SWIPE.fling },
    canStart: ({ target }) => !closestSelector(target, "button, a"),
    onStart: () => {
      swiping = true;
      setTransitioning(false);
    },
    onMove: (sample) => {
      setTransitioning(false);
      // Swipe left (dx<0) pages to a NEXT day; rubber-band it when blocked at today.
      const blockedForward = sample.dx < 0 && !options.canGoNext();
      setDragX(blockedForward ? sample.dx * CALENDAR_SWIPE.commitThresholdRatio : sample.dx);
    },
    onEnd: (sample) => {
      const commitPx = Math.min(
        CALENDAR_SWIPE.commitThresholdPx,
        surfaceWidth() * CALENDAR_SWIPE.commitThresholdRatio,
      );
      const committed = sample.isFling || Math.abs(sample.dx) >= commitPx;
      if (committed) {
        // Swipe left (dx<0) → next day; swipe right (dx>0) → previous day.
        animateDay(sample.dx < 0 ? 1 : -1);
      } else {
        snapBack();
      }
      queueMicrotask(() => {
        swiping = false;
      });
    },
    onCancel: () => {
      snapBack();
      swiping = false;
    },
  });

  const handlers = pointerHandlers(recognizer);

  onCleanup(() => {
    recognizer.cancel();
  });

  return {
    dragX,
    transitioning,
    handlers,
    /** True while a horizontal swipe is engaged (suppress incidental taps). */
    isSwiping: () => swiping,
  };
}
